import { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Heading,
  Header,
  Content,
  Form,
  Text,
  TextArea,
  Button,
  ButtonGroup,
  LabeledValue,
  ListView,
  ListViewItem,
  Collection,
  SkeletonCollection,
} from "@react-spectrum/s2";
import { getQueryLoadingState } from "@app/utils";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import { useVideo } from "@app/hooks/videos";

const ADOBE_EMAIL_REGEX = /^[a-z0-9._%+-]+@adobe\.com$/i;

const parseEmails = (value) => [
  ...new Set(
    value
      .split(/[\s,;]+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean),
  ),
];

export const ShareVideoDialog = ({ videoId, onAccept }) => {
  const [value, setValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const video = useVideo(videoId);

  const sharedWith = new Map([
    [
      video.data?.createdBy,
      {
        email: video.data?.createdBy,
        label: video.data?.createdBy,
        role: "Owner",
      },
    ],
    ...(video.data?.sharedWith ?? []).map((email) => [
      email,
      { email, label: email },
    ]),
  ]);

  console.log([...sharedWith.values()]);

  const sharedWithLoadingState = getQueryLoadingState(video);

  const emails = parseEmails(value);
  const invalidEmails = emails.filter((item) => !ADOBE_EMAIL_REGEX.test(item));
  const isInvalid = invalidEmails.length > 0;
  const canSubmit = emails.length > 0 && !isInvalid && !isSaving;

  return (
    <Dialog>
      {({ close }) => (
        <>
          <Heading slot="title">Share project</Heading>
          <Content>
            <Form>
              <TextArea
                label="User emails"
                description="Separate multiple emails with commas, spaces or new lines"
                placeholder="name@adobe.com, other@adobe.com"
                value={value}
                onChange={setValue}
                isInvalid={isInvalid}
                errorMessage={`Invalid @adobe.com email: ${invalidEmails.join(", ")}`}
                isDisabled={isSaving}
                autoFocus
              />
              <LabeledValue
                label="Who has access"
                value={
                  <div className={style({ width: "full" })}>
                    <ListView
                      selectionMode="none"
                      isQuiet
                      loadingState={sharedWithLoadingState}
                    >
                      {Array.from(sharedWith.values()).map((item) => (
                        <ListViewItem
                          key={item.email}
                          id={item.email}
                          textValue={item.label}
                        >
                          <Text>
                            {item.label}
                            {!!item.role && (
                              <Text
                                styles={style({
                                  font: "detail-sm",
                                  marginStart: 4,
                                })}
                              >
                                {`(${item.role})`}
                              </Text>
                            )}
                          </Text>
                        </ListViewItem>
                      ))}
                      {/* <Collection items={Array.from(sharedWith.values())}>
                    {(item) => (
                      <ListViewItem id={item.email} textValue={item.label}>
                        {item.label}
                      </ListViewItem>
                    )}
                  </Collection> */}
                      {video.isLoading && (
                        <SkeletonCollection>
                          {() => (
                            <ListViewItem id="skeleton">Loading…</ListViewItem>
                          )}
                        </SkeletonCollection>
                      )}
                    </ListView>
                  </div>
                }
              />
            </Form>
          </Content>
          <ButtonGroup>
            <Button variant="secondary" isDisabled={isSaving} onPress={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!canSubmit}
              isPending={isSaving}
              onPress={async () => {
                setIsSaving(true);
                try {
                  await onAccept(emails);
                  close();
                } catch {
                  // Caller shows the error toast; keep the dialog open to retry.
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              Share
            </Button>
          </ButtonGroup>
        </>
      )}
    </Dialog>
  );
};

ShareVideoDialog.propTypes = {
  videoId: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
};
